import { MailOutlined, SlackOutlined } from '@ant-design/icons';
import { Checkbox, DatePicker, Form, Input, Select, Switch } from 'antd';
import dayjs from 'dayjs';
import { useContext, useEffect, useRef } from 'react';
import ProfileContext from 'services/profileContext';
import styles from 'styles/components/cycleSettings.module.scss';
import { REMINDERS_TRIGGER } from 'utils/constants';

const CycleSettings = ({
  cycle,
  onFinish,
  showReminders = true,
  formRef,
  disabled = false,
  focusDeadline = false,
}) => {
  const deadlineRef = useRef(null);
  const { profile } = useContext(ProfileContext);
  useEffect(() => {
    if (focusDeadline) {
      deadlineRef.current?.focus();
    }
  }, [focusDeadline]);

  const shouldShowManagerReview =
    cycle?.modules.find((m) => m.type === 'DOWNWARD') &&
    cycle?.modules.length > 1;
  return (
    <div>
      <div className={styles.settingsForm}>
        <Form
          disabled={cycle?.isArchived}
          form={formRef}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            cycleName: cycle?.name || 'My First Cycle',
            ...(shouldShowManagerReview
              ? {
                isManagerReviewAfterOthers:
                  cycle?.isManagerReviewAfterOthers || true,
              }
              : {}),
            deadline: cycle?.deadline
              ? dayjs(cycle.deadline, 'YYYY-MM-DD')
              : undefined,
            remindersChannels:
              cycle?.reminders?.channels.length > 1
                ? 'BOTH'
                : cycle?.reminders?.channels[0] || 'EMAIL',
            remindersTriggers:
              cycle?.reminders?.triggers.length > 0
                ? cycle?.reminders?.triggers.map((t) => t.type)
                : Object.keys(REMINDERS_TRIGGER),
            reviewVisibility: cycle?.reviewVisibility || 'IMMEDIATE',
          }}
        >
          <Form.Item
            label="Name of the cycle"
            name="cycleName"
            className={styles.inputSettings}
          >
            <Input placeholder="Name of the cycle" autoFocus={true} />
          </Form.Item>
          {shouldShowManagerReview && (
            <Form.Item
              className={styles.inputSettings}
              label="Send manager review after all others are submitted ?"
              name="isManagerReviewAfterOthers"
              extra={
                "The manager's review will be sent after all other participants have submitted their reviews."
              }
            >
              <Switch
                disabled={disabled || cycle?.isArchived}
                checkedChildren="Yes"
                unCheckedChildren="No"
              />
            </Form.Item>
          )}
          <Form.Item
            className={styles.inputSettings}
            style={
              focusDeadline
                ? {
                  backgroundColor: '#f9f5ff',
                  borderRadius: 8,
                }
                : {}
            }
            label="Deadline"
            name="deadline"
            extra={
              'You can change that at any point in time. This is a global deadline. Note that you can publish the cycle or schedule it when it’ll be created.'
            }
          >
            <DatePicker
              ref={deadlineRef}
              disabledDate={(current) => current && current < dayjs()}
              placeholder="Deadline"
              style={{ width: '200px' }}
            />
          </Form.Item>
          <Form.Item
            className={styles.inputSettings}
            style={
              focusDeadline
                ? {
                  backgroundColor: '#f9f5ff',
                  borderRadius: 8,
                }
                : {}
            }
            label="How reviews should be released?"
            name="reviewVisibility"
            extra={disabled ? 'Put the cycle in draft mode to change this setting' : ''}

          >
            <Select
              disabled={disabled || cycle?.isArchived}
              options={[
                {
                  label: 'Immediately released once submitted',
                  value: 'IMMEDIATE',
                },
                {
                  label: 'Manually released by employee\'s manager',
                  value: 'MANAGER_RELEASE',
                },
              ]}
            />
          </Form.Item>
          {showReminders && (
            <Form.Item
              label="Reminders"
              className={styles.inputSettings}
              name="remindersChannels"
              extra={
                <div>
                  <div>
                    Choose when and where people will receive reminders once the
                    cycle is launched.
                  </div>
                  <div>
                    Note that reminders are send only on week days to avoid
                    notifications on weekends.
                  </div>
                </div>
              }
            >
              <Select
                options={[
                  {
                    label: (
                      <div>
                        <MailOutlined /> By Email
                      </div>
                    ),
                    value: 'EMAIL',
                  },
                  {
                    label: (
                      <div>
                        <SlackOutlined /> By Slack
                        {!profile.workspace?.slack?.teamId && (
                          <span>&nbsp;(Slack workspace not connected)</span>
                        )}
                      </div>
                    ),
                    value: 'SLACK',
                    disabled: !profile.workspace?.slack?.teamId,
                  },
                  {
                    label: (
                      <div>
                        <MailOutlined /> By Email and <SlackOutlined /> By Slack
                        {!profile.workspace?.slack?.teamId && (
                          <span>&nbsp;(Slack workspace not connected)</span>
                        )}
                      </div>
                    ),
                    value: 'BOTH',
                    disabled: !profile.workspace?.slack?.teamId,
                  },
                ]}
              />
            </Form.Item>
          )}
          <Form.Item
            className={styles.inputSettings}
            label="Reminders schedule"
            name="remindersTriggers"
          >
            <Checkbox.Group
              options={Object.values(REMINDERS_TRIGGER).map((t) => ({
                label: t.label,
                value: t.type,
              }))}
            />
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default CycleSettings;
